(() => {
  if (customElements.get('yb-curated-videos')) return;

  const httpsURL = (value) => {
    if (typeof value !== 'string' || value.length > 4096 || /[\u0000-\u001f\u007f-\u009f]/.test(value)) return null;
    try {
      const url = new URL(value);
      if (url.protocol !== 'https:' || url.username || url.password || url.hash || (url.port && url.port !== '443')) return null;
      if ([...url.searchParams.keys()].some((key) => ['access_token', 'client_secret', 'appsecret_proof', 'authorization'].includes(key.toLowerCase()))) return null;
      return url;
    } catch { return null; }
  };
  const instagramURL = (value) => {
    const url = httpsURL(value);
    if (!url || !['instagram.com', 'www.instagram.com'].includes(url.hostname)) return '';
    const match = url.pathname.match(/^\/(p|reel|tv)\/([A-Za-z0-9_-]+)\/?$/);
    return match ? `https://www.instagram.com/${match[1]}/${match[2]}/` : '';
  };
  const mediaURL = (value) => {
    const url = httpsURL(value);
    if (!url) return '';
    if (['yeahbox.cc', 'www.yeahbox.cc'].includes(url.hostname)
      && url.pathname.startsWith('/cdn/shop/t/105/assets/')) {
      url.hostname = 'yeahbox.com';
    }
    const hosts = ['cdn.shopify.com', 'cdninstagram.com', 'fbcdn.net'];
    const ownerHosts = ['yeahbox.com', 'www.yeahbox.com', 'yeahbox.cc', 'www.yeahbox.cc'];
    return url.origin === location.origin || ownerHosts.includes(url.hostname) || hosts.some((host) => url.hostname === host || url.hostname.endsWith(`.${host}`)) ? url.href : '';
  };
  const postKey = (value) => instagramURL(value).split('/')[4] || '';
  const keysFor = (item) => [item.id && `id:${item.id}`, postKey(item.permalink) && `ig:${postKey(item.permalink)}`].filter(Boolean);
  const text = (value, limit = 160) => typeof value === 'string' ? value.trim().replace(/\s+/g, ' ').slice(0, limit) : '';
  const nativeSources = (values) => Array.isArray(values) ? values.slice(0, 20).map((value) => {
    const url = mediaURL(value?.url);
    const type = value?.mime_type || (value?.format === 'mp4' ? 'video/mp4' : '');
    return url && type === 'video/mp4' ? { url, mime_type: type,
      height: Number.isFinite(value.height) ? value.height : 0 } : null;
  }).filter(Boolean).sort((a, b) => Math.abs(a.height - 1080) - Math.abs(b.height - 1080)) : [];

  class CuratedVideos extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.rail = this.querySelector('[data-yb-rail]');
      this.dialog = this.querySelector('[data-yb-dialog]');
      if (!this.rail || !this.dialog) return;
      this.labels = {
        play: this.dataset.labelPlay,
        instagram: this.dataset.labelViewInstagram,
        watch: this.dataset.labelWatchOnInstagram,
        heading: this.dataset.labelHeading,
        failed: this.dataset.labelLoadFailed
      };
      this.addEventListener('click', (event) => this.onClick(event), options);
      this.rail.addEventListener('scroll', () => this.queueControls(), { ...options, passive: true });
      this.dialog.addEventListener('close', () => this.stopPlayback(), options);
      this.dialog.addEventListener('click', (event) => {
        if (event.target !== this.dialog) return;
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.dialog.close();
      }, options);
      document.addEventListener('visibilitychange', () => { if (document.hidden) this.video?.pause(); }, options);
      document.addEventListener('shopify:block:select', (event) => {
        const card = Array.from(this.rail.children).find((item) => item.dataset.blockId === event.detail?.blockId);
        if (card) this.rail.scrollTo({ left: card.offsetLeft - this.rail.offsetLeft, behavior: 'auto' });
      }, options);
      this.observer = new ResizeObserver(() => this.queueControls());
      this.observer.observe(this.rail);
      this.querySelectorAll('[data-feed-card]').forEach((card) => card.remove());
      if (this.manualCards) this.restoreManual();
      this.normaliseManual();
      this.manualCards ??= Array.from(this.rail.children).map((card) => card.cloneNode(true));
      this.updateControls();
      if (this.dataset.libraryFeedUrl) this.loadLibrary();
      else {
        this.removeAttribute('data-library-pending');
        if (this.dataset.feedUrl) this.loadFeed();
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      if (this.dialog?.open) this.dialog.close();
      this.stopPlayback(false);
    }

    normaliseManual() {
      this.rail.querySelectorAll('[data-yb-card]').forEach((card) => {
        const link = card.querySelector('[data-yb-play]');
        const permalink = instagramURL(card.dataset.permalink);
        const source = httpsURL(card.dataset.videoSrc)?.href || '';
        card.dataset.permalink = permalink;
        card.dataset.videoSrc = source;
        if (source || permalink) link.href = source || permalink;
        else card.remove();
        if (!source) {
          link.removeAttribute('aria-haspopup');
          link.setAttribute('aria-label', `${this.labels.watch}: ${card.querySelector('h3').textContent}`);
        }
        const instagram = card.querySelector('.yb-curated-videos__instagram');
        if (instagram && permalink) instagram.href = permalink;
        else instagram?.remove();
      });
      this.toggleAttribute('data-has-items', this.rail.children.length > 0);
    }

    restoreManual() {
      this.rail.replaceChildren(...this.manualCards.map((card) => card.cloneNode(true)));
      this.toggleAttribute('data-has-items', this.rail.children.length > 0);
    }

    async loadLibrary() {
      const url = httpsURL(this.dataset.libraryFeedUrl);
      if (!url) return this.libraryFailed();
      const controller = this.controller;
      if (!controller || controller.signal.aborted) return;
      this.setAttribute('data-library-pending', '');
      const request = new AbortController();
      const abortRequest = () => request.abort();
      controller.signal.addEventListener('abort', abortRequest, { once: true });
      const timer = setTimeout(() => request.abort(), 15000);
      const productId = /^\d{1,32}$/.test(this.dataset.productId || '')
        ? `gid://shopify/Product/${this.dataset.productId}` : '';
      url.searchParams.delete('cursor');
      url.searchParams.delete('product_id');
      if (productId) url.searchParams.set('product_id', productId);
      url.searchParams.set('limit', '100');
      const items = [];
      const cursors = new Set();
      let completed = false;
      try {
        for (let page = 0; page < 10; page += 1) {
          const response = await fetch(url.href, { signal: request.signal, credentials: 'omit',
            referrerPolicy: 'no-referrer', redirect: 'error' });
          if (!response.ok || Number(response.headers.get('content-length')) > 1048576) throw new Error('feed');
          const body = await response.text();
          if (body.length > 1048576) throw new Error('feed');
          const feed = JSON.parse(body);
          if (feed.version !== 1 || !Array.isArray(feed.items) || feed.items.length > 100
            || !feed.page_info || typeof feed.page_info.has_next_page !== 'boolean') throw new Error('feed');
          for (const raw of feed.items) {
            if (raw.enabled === false || raw.unavailable === true) continue;
            if (productId && Array.isArray(raw.products) && !raw.products.includes(productId)) continue;
            const item = this.normaliseLibraryItem(raw);
            if (item) items.push(item);
          }
          if (!feed.page_info.has_next_page) { completed = true; break; }
          const cursor = feed.page_info.next_cursor;
          if (typeof cursor !== 'string' || !cursor || cursor.length > 4096
            || /[\u0000-\u001f\u007f-\u009f]/.test(cursor) || cursors.has(cursor)) throw new Error('feed');
          cursors.add(cursor);
          url.searchParams.set('cursor', cursor);
        }
        if (!completed) throw new Error('feed');
        if (controller !== this.controller || controller.signal.aborted) return;
        const seen = new Set();
        const cards = items.filter((item) => {
          if (seen.has(item.id)) return false;
          seen.add(item.id);
          return true;
        }).map((item) => this.createCard(item));
        this.rail.replaceChildren(...cards);
        this.toggleAttribute('data-has-items', cards.length > 0);
        this.querySelector('[data-yb-status]').hidden = true;
        this.removeAttribute('data-library-pending');
        this.updateControls();
      } catch {
        if (controller === this.controller && !controller.signal.aborted && this.isConnected) this.libraryFailed();
      } finally {
        clearTimeout(timer);
        controller.signal.removeEventListener('abort', abortRequest);
      }
    }

    libraryFailed() {
      // Legacy cards cannot prove the current merchant visibility settings.
      this.rail.replaceChildren();
      this.removeAttribute('data-has-items');
      this.removeAttribute('data-library-pending');
      this.feedFailed();
      this.updateControls();
    }

    normaliseLibraryItem(item) {
      if (!item || !['instagram', 'shopify'].includes(item.source) || item.media_type !== 'VIDEO'
        || !/^[A-Za-z0-9_-]{1,80}$/.test(String(item.id || ''))) return null;
      const permalink = instagramURL(item.permalink);
      const sources = item.source === 'shopify' ? nativeSources(item.sources) : [];
      const source = sources[0]?.url || mediaURL(item.media_url);
      if (!source && !permalink) return null;
      return { id: String(item.id), permalink, source, sources,
        title: text(item.title) || this.labels.heading,
        thumbnail: mediaURL(item.thumbnail_url), time: Date.parse(item.timestamp) || 0 };
    }

    async loadFeed() {
      const url = httpsURL(this.dataset.feedUrl);
      if (!url) return this.feedFailed();
      const controller = this.controller;
      if (!controller || controller.signal.aborted) return;
      const request = new AbortController();
      const abortRequest = () => request.abort();
      controller.signal.addEventListener('abort', abortRequest, { once: true });
      let timedOut = false;
      const timer = setTimeout(() => { timedOut = true; request.abort(); }, 15000);
      try {
        const response = await fetch(url.href, { signal: request.signal, credentials: 'omit', referrerPolicy: 'no-referrer' });
        if (!response.ok || Number(response.headers.get('content-length')) > 1048576) throw new Error('feed');
        const body = await response.text();
        if (controller !== this.controller || controller.signal.aborted) return;
        if (body.length > 1048576) throw new Error('feed');
        const feed = JSON.parse(body);
        if (feed.version !== 1 || feed.username !== 'bin.5192' || !Array.isArray(feed.items)) throw new Error('feed');
        const items = feed.items.slice(0, 200).map((item) => this.normaliseItem(item)).filter(Boolean);
        items.sort((a, b) => b.time - a.time);
        this.hydrateManual(items);
        const pinned = (this.dataset.pinned || '').split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
        const ordered = [];
        pinned.forEach((pin) => {
          const match = items.find((item) => item.id === pin || (postKey(pin) && postKey(item.permalink) === postKey(pin)));
          if (match) ordered.push(match);
        });
        ordered.push(...items);
        const seen = new Set();
        this.rail.querySelectorAll('[data-yb-card]').forEach((card) => keysFor({ id: card.dataset.mediaId, permalink: card.dataset.permalink }).forEach((key) => seen.add(key)));
        const limit = Math.min(20, Math.max(1, Number(this.dataset.maxItems) || 20));
        let count = 0;
        for (const item of ordered) {
          const keys = keysFor(item);
          if (keys.some((key) => seen.has(key))) continue;
          keys.forEach((key) => seen.add(key));
          this.rail.append(this.createCard(item));
          if (++count >= limit) break;
        }
        this.toggleAttribute('data-has-items', this.rail.children.length > 0);
        this.querySelector('[data-yb-status]').hidden = true;
        this.updateControls();
      } catch (error) {
        if ((timedOut || error.name !== 'AbortError') && controller === this.controller && this.isConnected) this.feedFailed();
      } finally {
        clearTimeout(timer);
        controller.signal.removeEventListener('abort', abortRequest);
      }
    }

    normaliseItem(item) {
      if (!item || item.media_type !== 'VIDEO' || !/^[A-Za-z0-9_-]{1,80}$/.test(String(item.id || ''))) return null;
      const permalink = instagramURL(item.permalink);
      if (!permalink) return null;
      return {
        id: String(item.id), permalink,
        title: text(item.title) || text(item.caption) || this.labels.heading,
        thumbnail: mediaURL(item.thumbnail_url), source: mediaURL(item.media_url),
        time: Date.parse(item.timestamp) || 0
      };
    }

    hydrateManual(items) {
      this.rail.querySelectorAll('[data-yb-card]:not([data-feed-card])').forEach((card) => {
        const manualKeys = keysFor({ id: card.dataset.mediaId, permalink: card.dataset.permalink });
        const match = items.find((item) => keysFor(item).some((key) => manualKeys.includes(key)));
        if (!match) return;
        if (!card.dataset.mediaId) card.dataset.mediaId = match.id;
        const play = card.querySelector('[data-yb-play]');
        if (!card.dataset.videoSrc || card.dataset.feedHydratedSrc === card.dataset.videoSrc) {
          card.dataset.videoSrc = match.source;
          card.dataset.feedHydratedSrc = match.source;
          play.href = match.source || card.dataset.permalink;
          if (match.source) play.setAttribute('aria-haspopup', 'dialog');
          else play.removeAttribute('aria-haspopup');
          play.setAttribute('aria-label', `${match.source ? this.labels.play : this.labels.watch}: ${card.querySelector('h3').textContent}`);
        }
        const poster = card.querySelector('img');
        if (!poster && match.thumbnail) {
          play.prepend(this.createPoster(match.thumbnail));
          card.dataset.feedHydratedPoster = match.thumbnail;
        } else if (poster && card.dataset.feedHydratedPoster === poster.src && match.thumbnail) {
          poster.src = match.thumbnail;
          card.dataset.feedHydratedPoster = match.thumbnail;
        }
      });
    }

    createPoster(source) {
      const poster = document.createElement('img');
      poster.className = 'yb-curated-videos__poster';
      poster.src = source;
      poster.alt = '';
      poster.width = 750;
      poster.height = 1000;
      poster.loading = 'lazy';
      poster.decoding = 'async';
      poster.addEventListener('error', () => poster.remove(), { once: true, signal: this.controller.signal });
      return poster;
    }

    createCard(item) {
      const card = document.createElement('li');
      card.className = 'yb-curated-videos__card';
      card.dataset.ybCard = '';
      card.dataset.feedCard = '';
      card.dataset.mediaId = item.id;
      card.dataset.permalink = item.permalink;
      card.dataset.videoSrc = item.source;
      if (item.sources?.length) card.dataset.videoSources = JSON.stringify(item.sources);
      const play = document.createElement('a');
      play.className = 'yb-curated-videos__play';
      play.dataset.ybPlay = '';
      play.href = item.source || item.permalink;
      play.setAttribute('aria-label', `${item.source ? this.labels.play : this.labels.watch}: ${item.title}`);
      if (item.source) play.setAttribute('aria-haspopup', 'dialog');
      if (item.thumbnail) play.append(this.createPoster(item.thumbnail));
      const icon = document.createElement('span');
      icon.className = 'yb-curated-videos__play-icon';
      icon.setAttribute('aria-hidden', 'true');
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('width', '24');
      svg.setAttribute('height', '24');
      svg.setAttribute('focusable', 'false');
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', 'M8 5.5v13l10-6.5Z');
      path.setAttribute('fill', 'currentColor');
      svg.append(path);
      icon.append(svg);
      const title = document.createElement('h3');
      title.className = 'yb-curated-videos__card-title';
      title.textContent = item.title;
      play.append(icon, title);
      card.append(play);
      if (item.permalink) {
        const instagram = document.createElement('a');
        instagram.className = 'yb-curated-videos__instagram';
        instagram.href = item.permalink;
        instagram.target = '_blank';
        instagram.rel = 'noopener noreferrer';
        instagram.textContent = this.labels.instagram;
        const arrow = document.createElement('span');
        arrow.setAttribute('aria-hidden', 'true');
        arrow.textContent = '↗';
        instagram.append(arrow);
        card.append(instagram);
      }
      return card;
    }

    feedFailed() {
      const status = this.querySelector('[data-yb-status]');
      status.textContent = this.labels.failed;
      status.hidden = false;
    }

    onClick(event) {
      if (event.target.closest('[data-yb-close]')) return this.dialog.close();
      const direction = event.target.closest('[data-yb-previous]') ? -1 : event.target.closest('[data-yb-next]') ? 1 : 0;
      if (direction) {
        const card = this.rail.firstElementChild;
        const gap = parseFloat(getComputedStyle(this.rail).columnGap) || 0;
        this.rail.scrollBy({ left: direction * ((card?.getBoundingClientRect().width || this.rail.clientWidth) + gap), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        return;
      }
      const link = event.target.closest('[data-yb-play]');
      if (!link || !this.contains(link) || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const card = link.closest('[data-yb-card]');
      const source = httpsURL(card.dataset.videoSrc)?.href;
      if (!source || typeof this.dialog.showModal !== 'function') return;
      event.preventDefault();
      document.querySelectorAll('yb-curated-videos dialog[open]').forEach((dialog) => dialog.close());
      this.opener = link;
      this.querySelector('[data-yb-dialog-title]').textContent = card.querySelector('h3').textContent;
      const fallback = this.querySelector('[data-yb-fallback]');
      const permalink = instagramURL(card.dataset.permalink);
      fallback.hidden = !permalink;
      if (permalink) fallback.href = permalink;
      else fallback.removeAttribute('href');
      this.querySelector('[data-yb-unavailable]').hidden = true;
      const video = document.createElement('video');
      video.controls = true;
      video.playsInline = true;
      video.preload = 'metadata';
      const poster = card.querySelector('img')?.src;
      if (httpsURL(poster)) video.poster = poster;
      video.src = source;
      video.addEventListener('error', () => { if (this.video === video) this.querySelector('[data-yb-unavailable]').hidden = false; }, { signal: this.controller.signal });
      this.video = video;
      this.querySelector('[data-yb-player]').replaceChildren(video);
      this.dialog.showModal();
      video.play().catch(() => {});
    }

    stopPlayback(restore = true) {
      if (this.video) {
        this.video.pause();
        this.video.removeAttribute('src');
        this.video.load();
        this.video.remove();
        this.video = null;
      }
      if (restore && this.opener?.isConnected) this.opener.focus({ preventScroll: true });
      this.opener = null;
    }

    queueControls() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.updateControls());
    }

    updateControls() {
      if (!this.rail) return;
      const maximum = this.rail.scrollWidth - this.rail.clientWidth;
      this.querySelector('[data-yb-navigation]').hidden = maximum < 2;
      this.querySelector('[data-yb-previous]').disabled = this.rail.scrollLeft < 2;
      this.querySelector('[data-yb-next]').disabled = this.rail.scrollLeft >= maximum - 2;
    }
  }

  customElements.define('yb-curated-videos', CuratedVideos);
})();
